import { useMemo } from 'react';

import { useTargetingParameters, useTargetingParametersObservable } from './targeting';
import { useIsSelectedProviderInOnboarding } from '../hooks/useIsSelectedProviderInOnboarding';
import { DESKTOP_MEDIA_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import type { ProviderOnboardingParameters } from '../api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/models';

export const isWithMenuEditorControlValue = false;
export const isSelfServeMenuEnabledControlValue = false;
export const isOcrDraftEditingEnabledControlValue = false;

export const SSU_EXPERIMENT_EVENTS = {
  GOTO_MENU_EDITOR: 'menu_editor_experiment:cta_clicked',
  RETURN_TO_ONBOARDING: 'menu_editor_experiment:complete_signup_clicked',
  MENU_EDITOR_PUBLISHED: 'menu_editor_experiment:menu_published',
};

export interface MenuEditorParams {
  isOnboarding: boolean;
  isWithMenuEditor: boolean;
  isDesktop: boolean;
}

export function shouldEnableMenuEditor(params: MenuEditorParams): boolean {
  return params.isOnboarding && params.isDesktop && params.isWithMenuEditor;
}

export function useProviderOnboardingParameters(): NonNullable<ProviderOnboardingParameters> {
  return useTargetingParameters().provider_onboarding ?? {};
}

export function useWithMenuEditor(): boolean {
  return useProviderOnboardingParameters().withMenuEditor ?? isWithMenuEditorControlValue;
}

export function useMenuEditorExperimentEnabled(): boolean {
  return useProviderOnboardingParameters().withMenuEditor !== undefined;
}

export function useWithMenuEditorObservable(): boolean {
  return useTargetingParametersObservable(
    'provider_onboarding',
    'withMenuEditor',
    isWithMenuEditorControlValue,
  );
}

export function useIsSelfServeMenuEnabledObservable(): boolean {
  return useTargetingParametersObservable(
    'provider_onboarding',
    'enableSelfServeMenuCreation',
    isSelfServeMenuEnabledControlValue,
  );
}

export function useIsOcrDraftEditingEnabledObservable(): boolean {
  return useTargetingParametersObservable(
    'provider_onboarding',
    'enableOcrDraftEditing',
    isOcrDraftEditingEnabledControlValue,
  );
}

export function useShouldEnableMenuEditorParams(): MenuEditorParams {
  const isOnboarding = useIsSelectedProviderInOnboarding();
  const isWithMenuEditor = useWithMenuEditor();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);

  return useMemo(() => {
    return {
      isOnboarding,
      isWithMenuEditor,
      isDesktop,
    };
  }, [isOnboarding, isDesktop, isWithMenuEditor]);
}

export function useShouldEnableMenuEditorObservableParams(): MenuEditorParams {
  const isOnboarding = useIsSelectedProviderInOnboarding();
  const isWithMenuEditor = useWithMenuEditorObservable();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);

  return useMemo(() => {
    return {
      isOnboarding,
      isWithMenuEditor,
      isDesktop,
    };
  }, [isOnboarding, isDesktop, isWithMenuEditor]);
}

export function useShouldEnableMenuEditor(): boolean {
  return shouldEnableMenuEditor(useShouldEnableMenuEditorParams());
}

export function useShouldEnableMenuEditorObservable(): boolean {
  return shouldEnableMenuEditor(useShouldEnableMenuEditorObservableParams());
}
